import { useCallback } from 'react';

import type { CreateTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/create-transition-function/schema';
import { createFlowTransitionFunction } from '@polyai/stream/store/reducers/flows';

import type { PickerOption } from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import TransitionModalForm, {
  type TransitionModalFormValues,
} from 'components/molecules/Transitions/TransitionModalForm';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import useProjectRoute from 'hooks/useProjectRoute';
import { generateTransitionId } from 'lib/prettyUuid';
import { getDefaultTransition } from 'screens/FlowDetails/hooks/helpers';
import useFlowDetailsStore from 'screens/FlowDetails/hooks/useFlowDetailsStore';

interface StepSetupPopupProps {
  onCreate?: (
    data: CreateTransitionFunctionPayload['transitionFunction'],
  ) => void;
  onCancel?: () => void;
  option?: PickerOption;
}

export const StepSetupPopup = ({
  onCancel,
  onCreate,
  option,
}: StepSetupPopupProps) => {
  const { activeStepId } = useFlowDetailsStore();
  const { flowId } = useProjectRoute<{
    flowId: string;
  }>();
  const serverDispatch = useServerDispatch();

  const createTransition = useCallback(
    ({
      transitionName,
      toStepName,
    }: {
      transitionName: string;
      toStepName?: string;
    }) => {
      if (!flowId) {
        return undefined;
      }

      const id = generateTransitionId();

      const newTransition = getDefaultTransition({
        id,
        transitionName: transitionName,
        toStepName: toStepName,
        fromStepId: activeStepId,
      });

      void serverDispatch(
        createFlowTransitionFunction({
          flowId,
          transitionFunction: newTransition,
        }),
      );

      return newTransition;
    },
    [activeStepId, serverDispatch, flowId],
  );

  const handleCreate = (data: TransitionModalFormValues) => {
    const transition = createTransition(data);

    if (!transition) {
      return;
    }

    onCreate?.(transition);
  };

  const sanitizeName = (name: string) =>
    `go_to_${name.toLowerCase().split(' ').join('_')}`;

  return (
    <TransitionModalForm
      defaultValues={{
        toStepName: option?.name,
        transitionName: sanitizeName(option!.name) ?? '',
      }}
      open
      onClose={() => onCancel?.()}
      onSubmit={handleCreate}
    />
  );
};
